import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { t } from "i18next";
import { translationsWithFallback } from "helpers/utils";
import { toast } from "react-toastify";

import type { IBeneficiaryDocumentType } from "./beneficiaryDocumentTypesStore";
import type { TFetchStatus } from "./types";

export type TCellStatus = "missing" | "valid" | "expiring_soon" | "expired";

export interface IDocumentCell {
    status: TCellStatus;
    validFrom: string | null;
    validUntil: string | null;
    documentId: number | null;
    /** An upload of this type awaits qualification. `status` stays the tracked one. */
    hasPending: boolean;
}

export interface IDocumentsOverviewEntry {
    id: number;
    name: string | null;
    surname: string | null;
    pseudonym: string | null;
    email: string | null;
    avatarUrl: string | null;
    lang: string | null;
    accountStatus: string | null;
    lastDocumentReminderAt: string | null;
    /** Documents uploaded by the author still to qualify */
    pendingCount: number;
    /** One cell per applicable active type, keyed by type id */
    documents: Record<number, IDocumentCell>;
    outdated: boolean;
}

/** Company-wide figures of the view header, independent of the filters */
export interface IDocumentsOverviewSummary {
    total: number;
    outdated: number;
    expiringSoon: number;
    pending: number;
}

export type TOverviewParams = {
    offset?: number;
    limit?: number;
    query?: string;
    documentTypeId?: number;
    status?: TCellStatus;
    onlyOutdated?: "1";
    hasPending?: "1";
    reminder?: "never" | `olderThan:${number}`;
    sortBy?: "name" | "lastDocumentReminderAt" | "pendingCount";
    sortOrder?: "asc" | "desc" | "ASC" | "DESC";
};

export const fetchDocumentsOverview = createAsyncThunk(
    "beneficiaryDocumentsOverview/fetch",
    async (params: TOverviewParams, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/documents-overview`,
                { params },
            );
            return response.data.data as {
                count: number;
                offset: number;
                entries: IDocumentsOverviewEntry[];
                types: IBeneficiaryDocumentType[];
                summary: IDocumentsOverviewSummary;
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const sendDocumentsReminder = createAsyncThunk(
    "beneficiaryDocumentsOverview/sendReminder",
    async (
        payload: { beneficiaryIds: number[]; subject: string; body: string; mailCc?: string[] },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/documents-reminder`,
                payload,
            );
            const result = response.data.data as { sent: number[]; skipped: number[] };
            if (result.sent.length)
                toast.success(`${t("BeneficiaryDocuments.Table.ReminderSent", { count: result.sent.length })}`);
            if (result.skipped.length)
                toast.warning(`${t("BeneficiaryDocuments.Table.ReminderSkipped", { count: result.skipped.length })}`);

            return result;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

interface State {
    entries: IDocumentsOverviewEntry[];
    count: number;
    types: IBeneficiaryDocumentType[];
    summary: IDocumentsOverviewSummary | null;
    status: TFetchStatus;
    reminderStatus: TFetchStatus;
}

const initialState: State = {
    entries: [],
    count: 0,
    types: [],
    summary: null,
    status: "idle",
    reminderStatus: "idle",
};

const slice = createSlice({
    name: "beneficiaryDocumentsOverview",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchDocumentsOverview.pending, (state) => {
                state.status = "pending";
            })
            .addCase(fetchDocumentsOverview.fulfilled, (state, { payload }) => {
                state.status = "fulfilled";
                state.entries = payload.entries;
                state.count = payload.count;
                state.types = payload.types;
                state.summary = payload.summary;
            })
            .addCase(fetchDocumentsOverview.rejected, (state, { payload }) => {
                state.status = "rejected";
                toast.error(translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError"), {
                    toastId: "documents-overview",
                });
            })
            .addCase(sendDocumentsReminder.pending, (state) => {
                state.reminderStatus = "pending";
            })
            .addCase(sendDocumentsReminder.fulfilled, (state, { payload }) => {
                state.reminderStatus = "fulfilled";
                const now = new Date().toISOString();
                state.entries = state.entries.map((entry) =>
                    payload.sent.includes(entry.id) ? { ...entry, lastDocumentReminderAt: now } : entry,
                );
            })
            .addCase(sendDocumentsReminder.rejected, (state, { payload }) => {
                state.reminderStatus = "rejected";
                toast.error(translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError"), {
                    toastId: "documents-reminder",
                });
            });
    },
});

export default slice.reducer;
